import { useState } from 'react'
import type { CartItem } from '../types/display'

export interface Spotlight {
  item: CartItem
  index: number
  /** Bumps on every new change, so a re-scan of the same item replays. */
  seq: number
}

const NONE: CartItem[] = []

const same = (a: CartItem, b: CartItem) =>
  a.title === b.title && a.quantity === b.quantity && a.cost === b.cost

/** The last line that differs: a scan appends, an edit changes in place. */
function changedIndex(before: CartItem[], after: CartItem[]): number {
  for (let i = after.length - 1; i >= 0; i--) {
    if (!before[i] || !same(before[i], after[i])) return i
  }
  return -1
}

/**
 * The line the till just changed: a new item, or one whose quantity or price
 * moved. A removal changes nothing, so the spotlight does not jump to an item
 * the customer did not just buy; an empty cart clears it. Derived while
 * rendering (state from the previous render), so the stage and the row light
 * up in the same frame.
 */
export function useSpotlight(items: CartItem[] | undefined): Spotlight | null {
  const list = Array.isArray(items) ? items : NONE
  const [previous, setPrevious] = useState<CartItem[]>(NONE)
  const [spotlight, setSpotlight] = useState<Spotlight | null>(null)

  if (list !== previous) {
    setPrevious(list)
    if (list.length === 0) {
      setSpotlight(null)
    } else if (list.length >= previous.length) {
      const index = changedIndex(previous, list)
      if (index >= 0) setSpotlight({ item: list[index], index, seq: (spotlight?.seq ?? 0) + 1 })
    }
  }
  return spotlight
}
